// Pay with PayPal button wireup.
//
// Currently supporting the iFrame approach which will be supplanted by a mini
// browser at some point in the future.

import {create as createBraintreeClient} from 'braintree-web/client'
import {create as createPayPalCheckout} from 'braintree-web/paypal-checkout'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'
import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'

const paypalStates = ['paypal-loading', 'paypal-logged-in', 'paypal-logged-out', 'paypal-down']

function displayPrettyPayPal(payload: {details: {email: string}}, paymentMethods: Element, state: string) {
  setPaypalState(paymentMethods, state)
  const container = document.getElementById('braintree-paypal-loggedin')!
  const paypalEmail = document.getElementById('bt-pp-email')!
  const paypalButton = document.getElementById('paypal-button-container')!
  paypalEmail.textContent = payload.details.email
  /* eslint-disable-next-line github/no-d-none */
  container.classList.remove('d-none')
  /* eslint-disable-next-line github/no-d-none */
  paypalButton.classList.add('d-none')
}

function hidePrettyPayPal(paymentMethods: Element, state: string) {
  setPaypalState(paymentMethods, state)
  const container = document.getElementById('braintree-paypal-loggedin')!
  const paypalButton = document.getElementById('paypal-button-container')!
  const paypalEmail = document.getElementById('bt-pp-email')!
  /* eslint-disable-next-line github/no-d-none */
  container.classList.add('d-none')
  paypalEmail.textContent = ''
  /* eslint-disable-next-line github/no-d-none */
  paypalButton.classList.remove('d-none')
  paypalButton.removeAttribute('disabled')
}

function setPaypalState(container: Element, state: string) {
  container.classList.remove(...paypalStates)
  if (paypalStates.indexOf(state) >= 0) {
    container.classList.add(state)
  }
}

async function getClientToken(paymentMethods: Element): Promise<string> {
  const token = paymentMethods.getAttribute('data-token')
  if (token) return token
  const response = await fetch('/account/billing/client_token', {
    headers: {
      ...getBaseFetchHeaders(),
    },
  })
  if (!response.ok) {
    // Handle specific error cases
    if (response.status === 503) {
      try {
        const errorData = await response.json()
        throw new Error(errorData.error)
      } catch {
        throw new Error('Payment service temporarily unavailable')
      }
    }

    throw new Error('Failed to get payment token')
  }
  const tokenText = await response.text()
  if (!tokenText.trim()) {
    throw new Error('Invalid payment token received')
  }
  return tokenText
}

async function setupBraintreePaypal(container: Element) {
  const paymentMethods = container.closest<HTMLElement>('.js-payment-methods')!
  if (paymentMethods.classList.contains('paypal-loading')) {
    return
  }

  if (paymentMethods.classList.contains('paypal-logged-in')) {
    return
  }
  container.textContent = ''
  hidePrettyPayPal(paymentMethods, 'paypal-loading')

  try {
    const token = await getClientToken(paymentMethods)

    if (!paymentMethods.hasAttribute('data-token')) {
      paymentMethods.setAttribute('data-token', token)
    }

    const selector = container.getAttribute('data-nonce-field')!
    const nonceField = paymentMethods.querySelector<HTMLElement>(selector)!

    const clientInstance = await createBraintreeClient({
      authorization: token,
    })
    const paypalCheckoutInstance = await createPayPalCheckout({
      client: clientInstance,
    })
    await paypalCheckoutInstance.loadPayPalSDK({
      vault: true,
    })

    await window.paypal
      .Buttons({
        fundingSource: 'paypal',
        style: {
          shape: 'sharp',
          color: 'blue',
          disableMaxHeight: true,
          disableMaxWidth: true,
          displayOnly: ['vaultable'],
        },
        createBillingAgreement() {
          return paypalCheckoutInstance.createPayment({flow: 'vault'})
        },

        onApprove: async (data, _actions) => {
          const payload = await paypalCheckoutInstance.tokenizePayment(data)

          if (nonceField) {
            nonceField.setAttribute('value', payload.nonce)
          }

          displayPrettyPayPal(payload, paymentMethods, 'paypal-logged-in')

          const awaitingPayment = document.querySelector<HTMLElement>('.js-awaiting-payment')
          if (awaitingPayment) {
            if (
              awaitingPayment.getAttribute('data-toggle-plan-upgrade-visibility') === 'true' &&
              awaitingPayment.hidden === true
            ) {
              awaitingPayment.hidden = false
            }
          }
        },

        onInit: () => {
          // Handle when the button is initialized
          hidePrettyPayPal(paymentMethods, 'paypal-logged-out')
        },

        onError: err => {
          setPaypalState(paymentMethods, 'paypal-down')
          if (err) {
            throw new Error(err)
          }
        },
      })
      .render('#paypal-button-container')

    const icon = document.querySelector('.js-paypal-cancel-icon')!.cloneNode(true)
    if (icon instanceof Element) {
      /* eslint-disable-next-line github/no-d-none */
      icon.classList.remove('d-none')
    }
  } catch (error) {
    setPaypalState(paymentMethods, 'paypal-down')
    throw error
  }
}

on('click', '#bt-pp-cancel', event => {
  const paymentMethods = event.currentTarget.closest<HTMLElement>('.js-payment-methods')!
  hidePrettyPayPal(paymentMethods, 'paypal-logged-out')
})
// Setup and render the paypal button if active.
observe('.js-paypal-container', {
  add(el) {
    if (el.closest('.active') != null) {
      setupBraintreePaypal(el)
    }
  },
})
